
import React from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";
import journalPrompts, { twistPrompts, lensFocusPrompts } from "@/data/journalPrompts";
import { PromptStage } from "@/hooks/dice/useRollerState";

interface PromptDisplayProps {
  diceValue: number;
  className?: string;
  promptStage: PromptStage;
  twistActive?: boolean;
  lensType?: string;
}

const PromptDisplay: React.FC<PromptDisplayProps> = ({ 
  diceValue,
  className,
  promptStage,
  twistActive,
  lensType
}) => {
  const { darkMode } = useSettings();

  // Get prompts from our centralized journalPrompts file
  const diceValuePrompts = diceValue && diceValue > 0 && diceValue <= 6 ? journalPrompts[diceValue] : null;

  
  const twist = twistPrompts[diceValue as keyof typeof twistPrompts];
  const lensFocus = lensType ? lensFocusPrompts[lensType as keyof typeof lensFocusPrompts] : null;

  // Animation variants for smoother transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.3 } },
  };

  const promptVariants = {
    hidden: { opacity: 0, height: 0, y: 20 },
    visible: { 
      opacity: 1, 
      height: "auto", 
      y: 0,
      transition: { 
        opacity: { duration: 0.4 }, 
        height: { duration: 0.4 }, 
        y: { duration: 0.3 } 
      } 
    },
    exit: {
      opacity: 0,
      height: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  // Don't render anything if there are no prompts or the stage is none
  // Fix the type error by using proper comparison for PromptStage type
  const shouldRender = diceValuePrompts && promptStage !== ("none" as PromptStage);
  if (!shouldRender) {
    return null;
  }

  return (
    <div className={cn(
      "text-center space-y-4",
      className
    )}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-4"
      >
        <AnimatePresence>
          {promptStage !== ("none" as PromptStage) && (
            <motion.div 
              key="perception"
              variants={promptVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "border-l-4 border-[#1E0C46] p-4 rounded-lg shadow-sm overflow-hidden text-left",
                darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
              )}
            >
              <h3 className={cn(
                "text-center font-semibold mb-2",
                darkMode ? "text-[#AE78F0]" : "text-[#1E0C46]"
              )}>
                What's Pulling at You...
              </h3>
              <p className={darkMode ? "text-[#FBF3F1] font-medium" : "text-gray-800"}>
                {diceValuePrompts.perception}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {((promptStage === "intention" || promptStage === "nextStep" || promptStage === "twist" || promptStage === "lensFocus")) && (
            <motion.div 
              key="intention"
              variants={promptVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "border-l-4 border-[#2F6665] p-4 rounded-lg shadow-sm overflow-hidden text-left",
                darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
              )}
            >
              <h3 className="text-[#2F6665] font-medium text-sm uppercase tracking-wider mb-2 text-center">
                Anchor It To Your Intention
              </h3>
              <p className={cn(
                "font-normal italic",
                darkMode ? "text-[#FBF3F1]" : "text-gray-700"
              )}>
                {diceValuePrompts.intention}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {((promptStage === "nextStep" || promptStage === "twist" || promptStage === "lensFocus")) && (
            <motion.div 
              key="nextStep"
              variants={promptVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "border-l-4 border-[#4A7B9D] p-4 rounded-lg shadow-sm overflow-hidden text-left",
                darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
              )}
            >
              <h3 className="text-[#4A7B9D] font-medium text-sm uppercase tracking-wider mb-2 text-center">
                Take a Next Step
              </h3>
              <p className={cn(
                "font-normal italic",
                darkMode ? "text-[#FBF3F1]" : "text-gray-700"
              )}>
                {diceValuePrompts.nextStep}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {promptStage === "twist" && twist && twistActive && (
            <motion.div 
              key="twist"
              variants={promptVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "border-l-4 border-[#9b87f5] p-4 rounded-lg shadow-sm overflow-hidden text-left",
                darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
              )}
            >
              <h3 className="text-[#9b87f5] font-medium text-sm uppercase tracking-wider mb-2 text-center">
                {twist.title}
              </h3>
              <p className={cn(
                "font-normal italic",
                darkMode ? "text-[#FBF3F1]" : "text-gray-700"
              )}>
                {twist.prompt}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {promptStage === "lensFocus" && lensFocus && (
            <motion.div 
              key="lensFocus"
              variants={promptVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "border-l-4 border-[#00FF99] p-4 rounded-lg shadow-sm overflow-hidden text-left",
                darkMode ? "bg-[#2d1a45]/90 text-[#FBF3F1]" : "bg-white"
              )}
            >
              <h3 className="text-[#00FF99] font-medium text-sm uppercase tracking-wider mb-2 text-center">
                Untangle: {lensFocus.title}
              </h3>
              <p className={cn(
                "font-normal italic",
                darkMode ? "text-[#FBF3F1]" : "text-gray-700"
              )}>
                {lensFocus.prompt}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default PromptDisplay;
